<template>
  <q-dialog
    ref="dialogRef"
    persistent
    @hide="onDialogHide">
    <q-card class="error-dialog">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6 text-negative">
          <q-icon :name="symRoundedError" class="q-mr-sm" size="28px" />
          {{ $t('crudmanager.errors.title') }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          :icon="symRoundedClose"
          flat
          round
          dense
          @click="onDialogCancel" />
      </q-card-section>

      <q-card-section>
        <div class="text-body1 q-mb-md">
          {{ errorMessage }}
        </div>
        
        <div
          v-if="errorDetails"
          class="text-caption text-grey-7 q-mb-md">
          <strong>{{ $t('crudmanager.errors.details') }}:</strong>
          <pre class="error-details">{{ errorDetails }}</pre>
        </div>

        <div
          v-if="suggestions.length > 0"
          class="q-mt-md">
          <div class="text-subtitle2 q-mb-sm">{{ $t('crudmanager.errors.suggestions.suggestions') }}:</div>
          <ul class="suggestions-list">
            <li
              v-for="suggestion in suggestions"
              :key="suggestion"
              class="q-mb-xs">
              {{ suggestion }}
            </li>
          </ul>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-close-popup
          :label="$t('crudmanager.errors.close')"
          color="primary"
          @click="onDialogCancel" />
        <q-btn
          v-if="showRetry"
          :label="$t('crudmanager.errors.retry')"
          color="secondary"
          @click="handleRetry" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { symRoundedClose, symRoundedError } from '@quasar/extras/material-symbols-rounded'
import { useDialogPluginComponent } from 'quasar'
import { useErrorDialog } from './composables/useErrorDialog'
import type { ErrorDialogProps } from './types'

const props = withDefaults(defineProps<ErrorDialogProps>(), {
  error: null,
  modelName: '',
  instanceId: '',
  onRetry: undefined,
})

// Use Quasar's dialog plugin component
defineEmits(useDialogPluginComponent.emits)
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

// Extract error formatting logic
const { errorMessage, errorDetails, suggestions, showRetry } = useErrorDialog(props)

/**
 * Handle retry action
 */
async function handleRetry() {
  if (props.onRetry) {
    try {
      await props.onRetry()
      // If retry succeeds, close the dialog
      onDialogOK()
    } catch (error) {
      // If retry fails, keep dialog open to show new error
      // The error will be handled by the caller
    }
  } else {
    // If no retry callback, just emit OK to close
    onDialogOK()
  }
}
</script>

<style scoped>
.error-dialog {
  min-width: 400px;
  max-width: 600px;
}

.error-details {
  background: var(--q-grey-2);
  padding: 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

.suggestions-list {
  margin: 0;
  padding-left: 20px;
}

.suggestions-list li {
  margin-bottom: 4px;
}
</style>

